import "./Accounts.css";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  CloudSync,
  Download,
  Info,
  Settings,
  Sync,
  SyncProblem,
} from "@mui/icons-material";
import Button from "../../../commons/Button/Button";
import Card, { Cards, CardText } from "../../../commons/Card/Card";
import { Content } from "../../../commons/Template/Template";
import Files from "../../../commons/Files/Files";
import { Form, FormLoader, Input } from "../../../commons/Form/Form";
import ImageContainer from "../../../commons/ImageContainer/ImageContainer";
import { PowensWebviewFlow } from "@powenscompany/webview-js";
import { VaultWrapper } from "../../Vault";
import api, {
  powensClassicAPI,
  powensAPIForSDK,
} from "../../../../api/resources";
import autoAccountImg from "../../../../assets/bank/auto.png";
import externalRoutes from "../../../../routes/externalRoutes";
import { formatAmount, formatDate } from "../../../../format";
import { formatDate as formatDateFNS } from "date-fns";
import { getMissingBankStatements } from "../../../../utils";
import { i18n } from "../../../../i18n";
import manualAccountImg from "../../../../assets/bank/manual.png";
import { useAuth } from "../../../../auth/AuthContext";
import { useModalDispatch } from "../../../commons/Modal/ModalContext";
import { useToastsDispatch } from "../../../commons/Toasts/ToastsContext";
import Tabs from "../../../commons/Tabs/Tabs";
import { ModalMessage } from "../../../commons/Modal/Modal";

export default function Accounts({ onSelect }) {
  const { t } = useTranslation(["common", "vault"]);
  document.title = t("vault:tabs.bank.title");
  const { accounts, setAccounts } = useAccounts([]);
  const [state, setState] = useState("normal"); // states: noBanking, pending, normal.
  const [loading, setLoading] = useState(true);
  const dispatchModal = useModalDispatch();
  const { activeCompany, loadingActiveCompany } = useAuth();
  useSuccess();

  const handleManage = (account) => {
    const modal = {
      title: t("vault:tabs.bank.content.modals.accountManage"),
      component: <AccountManagementModal account={account} />,
      onSuccess: () => init(),
    };
    dispatchModal({ type: "add", ...modal });
  };

  const init = async () => {
    setLoading(true);
    const response = await api.getAccounts();
    if (response.ok) {
      const jsonAccounts = await response.json();
      // Stabilise display order of accounts (in case of opening date change)
      const sortFn = (a, b) => (a.name > b.name ? 1 : -1); // alphabetical order
      const sortedAccounts = jsonAccounts.sort(sortFn);
      if (sortedAccounts.length > 0) {
        setAccounts(
          sortedAccounts.map((account) => ({
            id: account.id,
            name: account.name,
            balance: formatAmount(
              account.balance,
              i18n.resolvedLanguage,
              account.currency,
            ),
            bankName: account.bank.name,
            bankID: account.bank.ext_id,
            manual: !account.is_synced,
            // Ensure account is automatic in order to set `bankSynced`
            bankSynced: account.is_synced && account.bank.is_synced,
            statements: account.statements,
            active: !account.closing_date,
            openingDate: account.opening_date,
            updateDate: formatDate(account.update_date, i18n.resolvedLanguage),
            closingDate: formatDate(
              account.closing_date,
              i18n.resolvedLanguage,
            ),
            transactionsStartDate: account.starting_date
              ? formatDate(account.starting_date, i18n.resolvedLanguage)
              : null,
            missingMonths: account.missing_months,
          })),
        );
      } else if (activeCompany.has_open_banking) {
        setState("pending");
      } else {
        setState("noBanking");
      }

      setLoading(false);
    }
  };

  useEffect(() => {
    init();
  }, []);

  const { openPowensWebview } = usePowensWebview(() => {
    dispatchModal({ type: "success" });
    window.location.reload();
  });

  const [usePowensSDK] = useState(
    import.meta.env.VITE_POWENS_USE_SDK === "true",
  );

  const handleAddAccountAuto = async () => {
    const response = await powensClassicAPI.getAddBankURL();
    if (response.ok) {
      let jsonResponse = await response.json();
      window.location.href = jsonResponse.URL;
    }
  };

  const handleAddAccountAutoSDK = () =>
    openPowensWebview(!activeCompany.has_open_banking ? "Connect" : "Manage");

  const handleResyncBank = async (accountID) => {
    const response = await powensClassicAPI.getManageAccountURL(accountID);
    if (response.ok) {
      let jsonResponse = await response.json();
      window.location.href = jsonResponse.URL;
    }
  };

  const handleResyncBankSDK = (bankID) =>
    openPowensWebview("Reconnect", bankID);

  const updateStatements = async () => {
    const response = await api.getAccounts();
    if (response.ok) {
      let accounts = await response.json();

      if (accounts.length > 0) {
        setAccounts((prevAccounts) =>
          prevAccounts.map((prevAccount) => ({
            ...prevAccount,
            statements: accounts.filter(
              (account) => account.id === prevAccount.id,
            )[0].statements,
          })),
        );
      }
    }
  };

  const handleDownloadAll = () =>
    (window.location = externalRoutes.Banking.downloadStatements);

  const handleAddAccountManual = () => {
    const modal = {
      title: t("vault:tabs.bank.content.modals.accountCreate.title"),
      message: t("vault:tabs.bank.content.modals.accountCreate.message"),
      component: <ManualAccountModal />,
      size: "medium",
      onSuccess: () => window.location.reload(),
      className: "ManualAccount",
    };

    dispatchModal({ type: "add", ...modal });
  };

  const handleAddAccount = () => {
    const modal = {
      title: "Ajouter un compte bancaire",
      size: "medium",
      component: (
        <AccountChoiceModal
          onAuto={usePowensSDK ? handleAddAccountAutoSDK : handleAddAccountAuto}
          onManual={handleAddAccountManual}
        />
      ),
    };

    dispatchModal({ type: "add", ...modal });
  };

  if (loading || loadingActiveCompany) return <FormLoader />;
  return (
    <Content
      className={
        state === "pending" || state === "noBanking" ? "Vault-Bank_Banner" : ""
      }
    >
      {state === "noBanking" ? (
        <BankBanner text={t("vault:tabs.bank.content.paragraph.noBanking")} />
      ) : state === "pending" ? (
        <BankBanner text={t("vault:tabs.bank.content.paragraph.pending")} />
      ) : (
        <div className="Accounts">
          {accounts.map((account) => (
            <Account
              key={account.id}
              {...account}
              onManage={() => handleManage(account)}
              onUpdate={updateStatements}
              onResyncBank={
                usePowensSDK
                  ? () => handleResyncBankSDK(account.bankID)
                  : () => handleResyncBank(account.id)
              }
              onStartSync={handleAddAccountAuto}
              onSelect={() => onSelect(account.id)}
            />
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
        <Button
          text={
            state === "normal" || state === "pending"
              ? t("vault:tabs.bank.content.buttons.addAccount")
              : t("vault:tabs.bank.content.buttons.startBankSync")
          }
          variant="primary_a"
          onClick={handleAddAccount}
        />
        {state === "normal" &&
          accounts.some((account) => account.statements > 0) && (
            <Button
              text={t("vault:tabs.bank.content.buttons.downloadAll")}
              icon={<Download fontSize="small" />}
              variant={"secondary_b"}
              onClick={handleDownloadAll}
            />
          )}
      </div>

      <PowensWebview />
    </Content>
  );
}

function AccountManagementModal({ account }) {
  const { t } = useTranslation(["common", "vault"]);
  const tabs = [
    {
      id: "mod",
      title: t("common:modify"),
    },
    {
      id: "del",
      title: t("common:disable"),
    },
  ];

  const [selectedTab, setSelectedTab] = useState("mod");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Tabs tabs={tabs} selectedID={selectedTab} onSelect={setSelectedTab} />
      {selectedTab === "mod" && (
        <Fragment>
          <ModalMessage
            message={t("vault:tabs.bank.content.modals.accountMod.message")}
          />
          <ModifyAccountForm
            id={account.id}
            previousName={account.name}
            previousOpeningDate={account.openingDate}
          />
        </Fragment>
      )}
      {selectedTab === "del" && (
        <Fragment>
          <ModalMessage
            style={{ backgroundColor: "var(--color-error-2)" }}
            message={t("vault:tabs.bank.content.modals.accountClose.message")}
          />
          <AccountDisableForm id={account.id} />
        </Fragment>
      )}
    </div>
  );
}

function useSuccess() {
  const { t } = useTranslation(["common", "vault"]);
  let [searchParams] = useSearchParams();
  const dispatchToast = useToastsDispatch();

  const handleSuccess = () => {
    const searchParamsObj = Object.fromEntries(searchParams);
    const success = searchParamsObj["success"];

    if (success === "true") {
      dispatchToast({
        type: "add",
        variant: "success",
        heading: t("vault:tabs.bank.content.toasts.bankingSuccess.heading"),
        subheading: t(
          "vault:tabs.bank.content.toasts.bankingSuccess.subheading",
        ),
      });
    } else if (success === "false") {
      dispatchToast({
        type: "add",
        variant: "error",
        heading: t("vault:tabs.bank.content.toasts.bankingError.heading"),
        subheading: t("vault:tabs.bank.content.toasts.bankingError.subheading"),
      });
    }
  };

  useEffect(() => {
    handleSuccess();
  }, [searchParams]);
}

function useAccounts(
  init = [
    {
      id: 1,
      name: "Compte manuel",
      balance: "39 000€",
      bankName: "La Banque Postale",
      manual: true,
      bankSynced: false,
      active: true,
      selected: false,
      statements: 0,
    },
    {
      id: 2,
      name: "Compte manuel inactif",
      balance: "39 000€",
      bankName: "La Banque Postale",
      manual: true,
      bankSynced: false,
      active: false,
      selected: false,
      statements: 0,
    },
    {
      id: 3,
      name: "Compte automatique",
      balance: "39 000€",
      bankName: "La Banque Postale",
      updateDate: "12 décembre 2012",
      manual: false,
      bankSynced: true,
      active: true,
      selected: false,
      statements: 0,
    },
    {
      id: 4,
      name: "Compte automatique inactif",
      balance: "39 000€",
      bankName: "La Banque Postale",
      manual: false,
      bankSynced: false,
      active: false,
      selected: false,
      statements: 0,
    },
    {
      id: 5,
      name: "Compte automatique désynchronisé",
      balance: "39 000€",
      bankName: "La Banque Postale",
      updateDate: "12 décembre 2012",
      manual: false,
      bankSynced: false,
      active: true,
      selected: false,
      statements: 0,
    },
    {
      id: 6,
      name: "Compte automatique désynchronisé inactif",
      balance: "39 000€",
      bankName: "La Banque Postale",
      manual: false,
      bankSynced: false,
      active: false,
      selected: false,
      statements: 0,
    },
  ],
) {
  const [accounts, setAccounts] = useState(init);

  return { accounts, setAccounts };
}

function BankBanner({ text }) {
  return <p className="Vault-Bank_Banner-text">{text}</p>;
}

function Account({
  id = 0,
  name = "Compte",
  balance = "0 €",
  bankName = "Banque",
  updateDate = "12 décembre 2012",
  manual = false,
  bankSynced = false,
  active = false,
  statements = 0,
  onSelect,
  onResyncBank,
  onStartSync,
  onManage,
  onUpdate,
  openingDate,
  closingDate,
  transactionsStartDate,
  missingMonths,
}) {
  return (
    <div
      className={["Account", active ? "active" : "disabled"]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="Account-top">
        <div style={{ overflow: "hidden" }}>
          <AccountName name={name} />
          <AccountBankName bankName={bankName} />
        </div>

        {active && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <AccountSyncStatus
              onStartSync={onStartSync}
              manual={manual}
              bankSynced={bankSynced}
            />
            <AccountManage onManage={onManage} />
          </div>
        )}
      </div>

      <div className="Account-mid">
        {active ? (
          <Fragment>
            {!manual && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                  alignItems: "flex-start",
                }}
              >
                <AccountBalance balance={balance} />
                <TransactionsStartDate
                  transactionsStartDate={transactionsStartDate}
                />
                <AccountUpdateDate updateDate={updateDate} />
                {!bankSynced && (
                  <AccountSync
                    bankSynced={bankSynced}
                    onResyncBank={onResyncBank}
                  />
                )}
              </div>
            )}
          </Fragment>
        ) : (
          <div style={{ marginTop: "auto" }}>
            <AccountDisabled closingDate={closingDate} />
          </div>
        )}
      </div>
      <div className="Account-bottom">
        {!manual && <AccountTransactions onSelect={onSelect} />}

        <div style={{ marginLeft: "auto" }}>
          <AccountStatements
            statements={statements}
            id={id}
            name={name}
            openingDate={openingDate}
            handleUpdateFileCount={onUpdate}
            missingMonths={missingMonths}
          />
        </div>
      </div>
    </div>
  );
}

function AccountName({ name }) {
  return (
    <p title={name} style={{ fontWeight: "700" }} className="collapsibleText">
      {name}
    </p>
  );
}

function AccountBankName({ bankName }) {
  return (
    <p title={bankName} className="collapsibleText">
      {bankName}
    </p>
  );
}

function AccountSyncStatus({ onStartSync, manual, bankSynced }) {
  const { t } = useTranslation(["common", "vault"]);
  const style = {
    display: "flex",
    alignItems: "center",
  };
  return manual ? (
    <Button
      icon={<CloudSync />}
      title={t("vault:tabs.bank.content.card.account.startSync")}
      onClick={onStartSync}
    />
  ) : bankSynced ? (
    <span title={t("common:synced")} style={style}>
      <Sync style={{ color: "var(--color-success-1)" }} />
    </span>
  ) : (
    <span title={t("common:unsynced")} style={style}>
      <SyncProblem style={{ color: "var(--color-error-1)" }} />
    </span>
  );
}

function AccountSync({ onResyncBank }) {
  const { t } = useTranslation(["common", "vault"]);
  return (
    <Button
      onClick={onResyncBank}
      text={t("vault:tabs.bank.content.card.account.resync")}
      variant={"link"}
      style={{ color: "var(--color-error-1)" }}
    />
  );
}

function AccountManage({ onManage }) {
  const { t } = useTranslation(["common", "vault"]);
  return (
    <Button icon={<Settings />} title={t("common:manage")} onClick={onManage} />
  );
}

function AccountBalance({ balance }) {
  return <p style={{ fontWeight: "700", fontSize: "large" }}>{balance}</p>;
}

function AccountDisabled({ closingDate }) {
  const { t } = useTranslation(["common", "vault"]);
  return (
    <p style={{ fontSize: "small" }}>
      <Trans
        t={t}
        i18nKey="vault:tabs.bank.content.card.closingDate"
        values={{ closingDate: closingDate }}
      />
    </p>
  );
}

function AccountUpdateDate({ updateDate }) {
  const { t } = useTranslation(["common", "vault"]);
  return (
    <p
      className="collapsibleText"
      title={updateDate}
      style={{
        fontWeight: "500",
        fontSize: "small",
      }}
    >
      <Trans
        t={t}
        i18nKey="vault:tabs.bank.content.card.updateDate"
        values={{ updateDate: updateDate }}
      />
    </p>
  );
}

function TransactionsStartDate({ transactionsStartDate }) {
  const { t } = useTranslation(["common", "vault"]);
  return (
    <p
      className="collapsibleText"
      title={transactionsStartDate}
      style={{
        fontWeight: "500",
        fontSize: "small",
      }}
    >
      <Trans
        t={t}
        i18nKey="vault:tabs.bank.content.card.transactionsStartDate"
        context={!transactionsStartDate ? "none" : ""}
        values={{ transactionsStartDate: transactionsStartDate }}
      />
    </p>
  );
}

function AccountTransactions({ onSelect }) {
  const { t } = useTranslation(["common", "vault"]);
  return (
    <Button
      text={t("common:transactions")}
      variant={"link"}
      onClick={onSelect}
    />
  );
}

function AccountStatements({
  statements,
  id,
  name,
  openingDate,
  handleUpdateFileCount,
  missingMonths,
}) {
  const { t } = useTranslation(["common", "vault"]);
  const dispatchModal = useModalDispatch();

  const handleStatements = () => {
    dispatchModal({
      type: "add",
      title:
        t("vault:tabs.bank.content.modals.statements.title") + ` – ${name}`,
      message: t("vault:tabs.bank.content.modals.statements.message"),
      note:
        missingMonths.length > 0
          ? t("vault:tabs.bank.content.modals.statements.note")
          : null,
      size: "big",
      component: (
        <ManageStatements
          accountID={id}
          accountDate={openingDate}
          onUpdateFileCount={handleUpdateFileCount}
          missingMonths={missingMonths}
        />
      ),
    });
  };

  const text = `${t("common:statements")} (${statements}/12)`;
  return (
    <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
      {missingMonths.length > 0 && (
        <Info style={{ fontSize: "larger", color: "var(--color-warning-1)" }} />
      )}
      <Button text={text} variant={"link"} onClick={handleStatements} />
    </div>
  );
}

function ModifyAccountForm({ id, previousName, previousOpeningDate }) {
  const dispatchModal = useModalDispatch();

  const { t } = useTranslation(["common", "vault"]);

  const [account, setAccount] = useState({
    name: previousName,
    opening_date: previousOpeningDate || null,
  });

  const handleAccountChange = (newValue, field) => {
    setAccount((prevAccount) => ({
      ...prevAccount,
      [field]: newValue,
    }));
  };

  const initialErrors = {
    name: [],
    opening_date: [],
    non_field_errors: [],
  };

  const [accountErrors, setAccountErrors] = useState(initialErrors);

  const handleSubmit = async () => {
    setAccountErrors(initialErrors);

    const response = await api.changeAccountProps(id, account);
    if (response.ok) {
      dispatchModal({ type: "success" });
    } else {
      const jsonErrors = await response.json();
      setAccountErrors(jsonErrors);
    }
  };

  const nameInput = {
    label: t("vault:tabs.bank.content.modals.accountMod.component.name"),
    name: "name",
    type: "text",
    value: account.name,
    onChange: (newValue) => handleAccountChange(newValue, "name"),
    errors: accountErrors.name,
  };

  const dateInput = {
    label: t("vault:tabs.bank.content.modals.accountMod.component.openingDate"),
    name: "opening_date",
    type: "date",
    value: account.opening_date,
    onChange: (newValue) => handleAccountChange(newValue, "opening_date"),
    errors: accountErrors.opening_date,
    required: false,
  };

  return (
    <Form onSubmit={handleSubmit} errors={accountErrors.non_field_errors}>
      <Input {...nameInput} />
      <Input {...dateInput} />
    </Form>
  );
}

function ManageStatements({
  accountID,
  accountDate,
  onUpdateFileCount,
  missingMonths,
}) {
  const { t } = useTranslation(["common", "vault"]);
  const { activeCompany, loadingActiveCompany } = useAuth();
  const dispatchToast = useToastsDispatch();

  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [newFiles, setNewFiles] = useState([]);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [fileCountChange, setFileCountChange] = useState(false);

  useEffect(() => {
    if (fileCountChange) {
      onUpdateFileCount(files.length);
      setFileCountChange(false);
    }
  }, [fileCountChange]);

  // Init options
  const initOptions = useCallback(async () => {
    setSelectedPeriod(null);
    const response = await api.getBankStatements(accountID);
    if (response.ok && !loadingActiveCompany) {
      const statements = await response.json();
      const periods = getMissingBankStatements(
        statements,
        [activeCompany.immatriculation_date, accountDate]
          .filter((el) => el)
          .sort()
          .pop(),
      );
      setPeriods(periods);
      setLoadingPeriods(false);
    }
  }, [accountID, loadingActiveCompany, activeCompany.immatriculation_date]);

  useEffect(() => {
    initOptions();
  }, [initOptions]);

  // Delete file then: init options
  const handleFileDelete = useCallback(
    async (fileID) => {
      const response = await api.deleteBankStatement(accountID, fileID);
      if (response.ok) {
        setFiles((prevFiles) =>
          prevFiles.filter((prevFile) => prevFile.id !== fileID),
        );
        initOptions();
        setFileCountChange(true);
        return true;
      }
      return false;
    },
    [accountID, initOptions],
  );

  // Download files
  const handleFileDownload = useCallback(async () => {
    const response = await api.getBankStatements(accountID);
    if (response.ok) {
      const files = await response.json();

      setFiles(
        files.map((file) => ({
          id: file.id,
          name: formatDate(file.period, i18n.resolvedLanguage, "year-month"),
          date: new Date(
            file.period.split("-")[0],
            file.period.split("-")[1] - 1,
          ),
          url: file.file,
          onDelete: () => handleFileDelete(file.id),
        })),
      );
      setLoadingFiles(false);
      setFileCountChange(true);
    }
  }, [accountID, handleFileDelete]);

  useEffect(() => {
    handleFileDownload();
  }, [handleFileDownload]);

  // Upload files then: reset states (upload success, new files), download files and init options
  const handleFileUpload = async () => {
    const data = new FormData();
    data.append("file", newFiles[0]);
    data.append("period", selectedPeriod.value);

    const response = await api.createBankStatement(accountID, data);
    if (response.ok) {
      dispatchToast({
        type: "add",
        variant: "success",
        heading: t("vault:common.toasts.fileUploadSuccess.heading"),
        subheading: t("vault:common.toasts.fileUploadSuccess.subheading"),
      });
      setUploadSuccess(true);
      setNewFiles([]);
      handleFileDownload();
      initOptions();
    } else {
      const errors = await response.json();
      dispatchToast({
        type: "add",
        variant: "warning",
        heading: errors.file[0],
      });
      setUploadSuccess(true);
    }
  };

  const inputs = [
    {
      type: "select",
      required: true,
      placeholder: t("vault:tabs.bank.content.files.options.placeholder"),
      onChange: setSelectedPeriod,
      value: selectedPeriod,
      options:
        !loadingPeriods &&
        periods.map((period) => ({
          label: formatDate(period, i18n.resolvedLanguage, "year-month"),
          value: period,
          className: missingMonths.find((month) => month === period)
            ? "highlightOption"
            : "",
        })),
      autoSortOptionsDisabled: true,
      noOptionsMessage: t("vault:selectNoOptions"),
    },
    {
      type: "file",
      value: newFiles,
      onChange: setNewFiles,
    },
  ];

  const form = {
    inputs: [
      {
        data: inputs,
      },
    ],
    button: t("vault:common.fileDrop.upload"),
    onSubmit: handleFileUpload,
    isButtonDisabled: newFiles.length === 0,
    className: "FileDropForm",
    resetLoader: uploadSuccess,
  };
  return (
    <VaultWrapper>
      <Form {...form} />
      <Files files={files} loadingFiles={loadingFiles} sortByDate />
    </VaultWrapper>
  );
}

function ManualAccountModal() {
  const { t } = useTranslation(["common", "vault"]);
  const dispatchModal = useModalDispatch();

  const [account, setAccount] = useState({
    bank_name: "",
    name: "",
    number: "",
    iban: "",
    currency: null,
    opening_date: null,
  });

  const initialErrors = {
    bank_name: [],
    name: [],
    number: [],
    iban: [],
    currency: [],
    opening_date: [],
    non_field_errors: [],
  };

  const [accountErrors, setAccountErrors] = useState(initialErrors);

  const handleAccountChange = (newValue, field) => {
    setAccount((prevAccount) => ({
      ...prevAccount,
      [field]: newValue,
    }));
  };

  const { activeCompany, loadingActiveCompany } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);

  const initCurrencies = async () => {
    const response = await api.getCurrencies();
    if (response.ok) {
      const jsonCurrencies = await response.json();
      const currenciesOptions = jsonCurrencies.map((currency) => ({
        label: currency,
        value: currency,
      }));
      setCurrencies(currenciesOptions);
      const activeCompanyCurrency = currenciesOptions.find(
        (currency) => currency.value === activeCompany.currency,
      );
      handleAccountChange(activeCompanyCurrency, "currency");
    }
  };

  const bankNameIn = {
    type: "text",
    required: true,
    name: "bank_name",
    label: t("common:bankName"),
    value: account.bank_name,
    onChange: (value) => handleAccountChange(value, "bank_name"),
    errors: accountErrors.bank_name,
  };

  const nameIn = {
    type: "text",
    required: true,
    name: "name",
    label: t("common:accountName"),
    value: account.name,
    onChange: (value) => handleAccountChange(value, "name"),
    errors: accountErrors.name,
  };

  const numberIn = {
    type: "text",
    required: true,
    name: "number",
    label: t("common:accountNumber"),
    value: account.number,
    onChange: (value) => handleAccountChange(value, "number"),
    errors: accountErrors.number,
  };

  const ibanIn = {
    type: "text",
    required: true,
    name: "iban",
    label: t("common:iban"),
    value: account.iban,
    onChange: (value) => handleAccountChange(value, "iban"),
    errors: accountErrors.iban,
  };

  const dateIn = {
    type: "date",
    required: true,
    name: "opening_date",
    label: t("common:openingDate"),
    value: account.opening_date,
    onChange: (value) => handleAccountChange(value, "opening_date"),
    errors: accountErrors.opening_date,
  };

  const currencyIn = {
    type: "radio",
    required: true,
    name: "currency",
    label: t("common:currency"),
    value: account.currency,
    onChange: (value) => handleAccountChange(value, "currency"),
    options: currencies,
    errors: accountErrors.currency,
    rowRadio: true,
  };

  const inputs = [
    {
      data: [bankNameIn],
    },
    {
      data: [nameIn, numberIn],
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
    },
    {
      data: [ibanIn, dateIn],
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
    },
    {
      data: [currencyIn],
    },
  ];

  const handleSubmit = async () => {
    setAccountErrors(initialErrors);

    const response = await api.createBankAccount({
      ...account,
      currency: account.currency.value,
    });
    if (response.ok) {
      dispatchModal({
        type: "success",
      });
    } else {
      const jsonErrors = await response.json();
      setAccountErrors(jsonErrors);
    }
  };

  const init = () => {
    setLoading(true);
    initCurrencies();
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  if (loading || loadingActiveCompany) return <></>;
  return (
    <Form
      inputs={inputs}
      onSubmit={handleSubmit}
      errors={accountErrors.non_field_errors}
    />
  );
}

function AccountChoiceModal({ onAuto, onManual }) {
  const buttonStyle = { display: "block", marginTop: "1rem" };

  const { t } = useTranslation(["common", "vault"]);
  return (
    <Cards className="AccountChoiceModalCards">
      <Card>
        <ImageContainer
          src={autoAccountImg}
          backgroundColor="var(--color-card-background)"
        />
        <CardText
          heading={t(
            "vault:tabs.bank.content.modals.accountChoice.auto.heading",
          )}
          subheading={t(
            "vault:tabs.bank.content.modals.accountChoice.auto.subheading",
          )}
        >
          <Button
            onClick={onAuto}
            variant={"primary_a"}
            text={t("common:sync")}
            style={buttonStyle}
          />
        </CardText>
      </Card>
      <Card>
        <ImageContainer src={manualAccountImg} />
        <CardText
          heading={t(
            "vault:tabs.bank.content.modals.accountChoice.manual.heading",
          )}
          subheading={t(
            "vault:tabs.bank.content.modals.accountChoice.manual.subheading",
          )}
        >
          <Button
            onClick={onManual}
            variant={"secondary_b"}
            text={t("common:create")}
            style={buttonStyle}
          />
        </CardText>
      </Card>
    </Cards>
  );
}

function AccountDisableForm({ id }) {
  const { t } = useTranslation(["common", "vault"]);
  const dispatchModal = useModalDispatch();

  const today = () => new Date().setHours(0, 0, 0, 0);
  const formatDateForInput = (date) => formatDateFNS(date, "yyyy-MM-dd");

  const initialErrors = {
    closing_date: [],
    non_field_errors: [],
  };

  const [account, setAccount] = useState({
    closing_date: "",
  });

  const handleAccountChange = (field, newValue) =>
    setAccount((prev) => ({
      ...prev,
      [field]: newValue,
    }));

  const [errors, setErrors] = useState(initialErrors);

  const clearErrors = () => {
    setErrors(initialErrors);
  };

  const closingDateInput = {
    type: "date",
    name: "closing_date",
    label: t("common:closing_date"),
    value: account.closing_date,
    onChange: (value) => handleAccountChange("closing_date", value),
    max: formatDateForInput(today()),
    errors: errors.closing_date,
  };

  const inputs = [
    {
      data: [closingDateInput],
    },
  ];

  const handleDisableAccount = async () => {
    clearErrors();
    const response = await api.changeAccountProps(id, account);
    if (response.ok) {
      dispatchModal({ type: "success" });
    } else {
      const jsonErrors = await response.json();
      setErrors(jsonErrors);
    }
  };

  return (
    <Form
      inputs={inputs}
      errors={errors.non_field_errors}
      onSubmit={handleDisableAccount}
      buttonVariant={"error"}
    />
  );
}

export function PowensWebview() {
  return (
    <div style={{ position: "absolute", zIndex: "5000" }}>
      <powens-webview />
    </div>
  );
}

export function usePowensWebview(onClose = () => {}) {
  const [showJson, setShowJson] = useState(false);
  const [jsonData, setJsonData] = useState("");

  useEffect(() => {
    const messageEventListener = (event) => {
      const allowedOrigins = [window.origin, "https://webview.powens.com"];
      if (!allowedOrigins.includes(event.origin)) {
        return;
      }

      if (event.data?.type !== "powensWebviewTermination") return;

      setShowJson(true);
      const powensResult = event.data?.data;
      setJsonData(JSON.stringify(powensResult, undefined, 2));

      onClose();
    };

    window.addEventListener("message", messageEventListener);
    return () => window.removeEventListener("message", messageEventListener);
  }, [onClose]);

  const openPowensWebview = async (flow, bankID) => {
    setShowJson(false);
    const powensWebview = document.querySelector("powens-webview");
    if (powensWebview) {
      let options = {
        flow: PowensWebviewFlow[flow],
        domain: import.meta.env.VITE_POWENS_DOMAIN,
        clientId: import.meta.env.VITE_POWENS_CLIENT_ID, // "leano" app id
        redirectUri:
          import.meta.env.VITE_API_URL + "/banking-api/call-back/powens",
      };
      if (flow === "Manage" || flow === "Reconnect") {
        const response = await powensAPIForSDK.getPowensCode();
        const jsonResponse = await response.json();
        options = {
          ...options,
          code: jsonResponse.code,
        };
      }
      if (flow === "Reconnect") {
        options = {
          ...options,
          connectionId: bankID,
        };
      }
      powensWebview.options = options;
      powensWebview.openWebview();
    } else {
      console.error("Powens webview element not found.");
    }
  };

  return { openPowensWebview, showJson, jsonData };
}
